import * as Yup from "yup";
import { isValidPhoneNumber } from "react-phone-number-input";

// Common Email Validation Rule
const emailValidation = Yup.string()
  .trim()
  .email("Please enter a valid email")
  .required("Please enter email")
  .matches(
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\.[a-zA-Z]{2,})?$/,
    "Please enter a valid email"
  );
// comman role management :
const roleValidation = Yup.string().required("Please select a role");

// Common full name Validation Rule
const fullNameValidation = Yup.string()
  .required("Please enter full name")
  .trim();
// Common Phone Number Validation Rule
const PhoneValidation = Yup.string()
  .required("Please enter phone number")
  .test("is-valid-phone", "Please enter valid phone number", (value) =>
    isValidPhoneNumber(value || "")
  );

// Common Password Validation Rule
const passwordValidation = Yup.string()
  .required("Please enter password")
  .min(8, "Password cannot be less than 8 characters")
  .matches(
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%&'*+-.,:;<=>?^_`{|}~])/,
    "Strong passwords require at least 1 lowercase letter, 1 uppercase letter, 1 number, and 1 special character."
  );

const firstNameValidation = Yup.string()
  .required("Please enter first name")
  .trim();
const lastNameValidation = Yup.string()
  .required("Please enter last name")
  .trim();

export const registerSchema = Yup.object().shape({
  email: emailValidation,
  first_name: firstNameValidation,
  last_name: lastNameValidation,
  linkedin_url: Yup.string().required("Please enter linkedin profile").trim(),
  phone_number: PhoneValidation,
  role: Yup.string().required("Please select current role"),
  source: Yup.string().required("Please select one"),
});

export const contactUsSchema = Yup.object().shape({
  email: emailValidation,
  full_name: fullNameValidation,
  phone_number: PhoneValidation,
  address: Yup.string().required("Please enter address").trim(),
  subject: Yup.string().required("Please select what this inquiry is about"),
  message: Yup.string().required("Please enter message").trim(),
});

export const createCoachPasswordSchema = Yup.object().shape({
  password: passwordValidation,
  confirm_password: Yup.string()
    .required("Please enter confirm password")
    .oneOf([Yup.ref("password"), null], "Your password must match"),
})

export const signInSchema = Yup.object().shape({
  email: emailValidation,
  password: passwordValidation,
});
export const adminSignInSchema = Yup.object().shape({
  email: emailValidation,
  password: passwordValidation,
});

export const forgotPasswordSchema = Yup.object().shape({
  email: emailValidation,
});

export const resetPasswordSchema = Yup.object().shape({
  newPassword: Yup.string()
    .required("Please enter password")
    .min(8, "Password cannot be less than 8 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%&'*+-.,:;<=>?^_`{|}~])/,
      "Strong passwords require at least 1 lowercase letter, 1 uppercase letter, 1 number, and 1 special character."
    ),
  confirm_password: Yup.string()
    .required("Please enter confirm password")
    .oneOf([Yup.ref("newPassword"), null], "Your password must match"),
});

export const changePasswordSchema = Yup.object().shape({
  current_password: Yup.string()
    .required("Please enter current password")
    .min(8, "Current password cannot be less than 8 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%&'*+-.,:;<=>?^_`{|}~])/,
      "Please enter a valid password"
    ),
  new_password: Yup.string()
    .required("Please enter new password")
    .min(8, "New password cannot be less than 8 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%&'*+-.,:;<=>?^_`{|}~])/,
      "Strong passwords require at least 1 lowercase letter, 1 uppercase letter, 1 number, and 1 special character."
    ),
  confirm_password: Yup.string()
    .required("Please enter confirm password")
    .oneOf([Yup.ref("new_password"), null], "Your password must match"),
});

export const step1Schema = Yup.object().shape({
  email: emailValidation,
  full_name: fullNameValidation,
  phone_number: PhoneValidation,
});

export const step2Schema = Yup.object().shape({
  category_id: Yup.string().required("Please select a coaching category"),
  format: Yup.string().required("Please select a coaching format"),
  year_of_experience: Yup.number()
    .typeError("Please enter a valid number")
    .required("Please enter your years of experience")
    .min(0, "Experience cannot be negative")
    .max(100, "Please enter a realistic experience (max 100 years)"),
});

export const step3Schema = Yup.object().shape({
  bio: Yup.string().required("Please enter bio").trim(),
  reason: Yup.string().required("Please enter reason").trim(),
  // tagline: Yup.string()
  //   .required("Please enter a tagline")
  //   .trim()
  //   .test(
  //     "min-words",
  //     "Tagline must be at least 50 words",
  //     (value) => {
  //       if (!value) return false; // required will handle empty
  //       const wordCount = value.trim().split(/\s+/).length; // count words
  //       return wordCount >= 50;
  //     }
  //   ),
  tagline: Yup.string().required("Please enter a tagline").trim(),
  profile_image: Yup.mixed()
    .required("Please upload your profile image")
    .test("fileType", "Please upload a valid image", (value) => {
      if (!value || typeof value === "string") return true;
      const validFileTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
      ];
      return validFileTypes.includes(value.type);
    }),
  cv: Yup.array()
    .required("Please upload at least one file")
    .min(1, "Please upload at least one file")
    .test("fileType", "Only PDF or Image files are allowed", (files) => {
      if (!files || files.length === 0) return false;

      const allowed = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
      ];

      return files.every((file) => allowed.includes(file.type));
    })
    .test("fileSize", "Each file must be less than 2 MB", (files) => {
      if (!files || files.length === 0) return false;

      return files.every((file) => file.size <= 2 * 1024 * 1024);
    }),

  certificate: Yup.array()
    .required("Please upload at least one file")
    .min(1, "Please upload at least one file")
    .test("fileType", "Only PDF or Image files are allowed", (files) => {
      if (!files || files.length === 0) return false;

      const allowed = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
      ];

      return files.every((file) => allowed.includes(file.type));
    })
    .test("fileSize", "Each file must be less than 2 MB", (files) => {
      if (!files || files.length === 0) return false;

      return files.every((file) => file.size <= 2 * 1024 * 1024);
    }),
});

export const step1BootCampSchema = Yup.object().shape({
  header_title: Yup.string().required("Please enter bootcamp heading").trim(),
  header_sub_title: Yup.string()
    .required("Please enter bootcamp sub heading")
    .trim(),
  start_date: Yup.date()
    .required("Please select a Bootcamp start date")
    .typeError("Please enter a valid date"),

  end_date: Yup.date()
    .required("Please select a Bootcamp end date")
    .test(
      "is-same-or-after-start",
      "End date must be the same or after the start date",
      function (value) {
        const { start_date } = this.parent;
        return value && start_date && value >= start_date;
      }
    )
    .typeError("Please enter a valid date"),
  location: Yup.string().required("Please enter Bootcamp location").trim(),
  header_description: Yup.string()
    .required("Please enter Bootcamp description")
    .trim(),
});

export const step3BootCampSchema = Yup.object().shape({
  footer_title: Yup.string()
    .required("Please enter Bootcamp sub heading")
    .trim(),
  footer_sub_title: Yup.string()
    .required("Please enter Bootcamp sub title")
    .trim(),
  footer_description: Yup.string()
    .required("Please enter Bootcamp sub description")
    .trim(),
  footer_image: Yup.mixed()
    .required("Please upload image")
    .test("fileType", "Please upload a valid image", (value) => {
      if (!value || typeof value === "string") return true;
      const validFileTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
      ];
      return validFileTypes.includes(value.type);
    }),
  brochure: Yup.mixed()
    .required("Please upload image")
    .test("fileType", "Please upload a valid image", (value) => {
      if (!value || typeof value === "string") return true;
      const validFileTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
      ];
      return validFileTypes.includes(value.type);
    }),
  header_image: Yup.mixed()
    .required("Please upload your Bootcamp image")
    .test("fileType", "Please upload a valid image", (value) => {
      if (!value || typeof value === "string") return true;
      const validFileTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
      ];
      return validFileTypes.includes(value.type);
    }),
});

export const bootcampPurchaseSchema = Yup.object().shape({
  email: emailValidation,
  phone_number: PhoneValidation,
  first_name: firstNameValidation,
  last_name: lastNameValidation,
});

export const bootcampWaittingListSchema = Yup.object().shape({
  email: emailValidation,
  phone_number: PhoneValidation,
  name: Yup.string().trim().required("Please enter name"),
  notes: Yup.string().trim().optional(),
});

export const becomeCoachSchema = Yup.object().shape({
  email: emailValidation,
  full_name: fullNameValidation,
  phone_number: PhoneValidation,
  category_id: Yup.string().required("Please select a coaching category"),
  format: Yup.string().required("Please select a coaching format"),
  year_of_experience: Yup.number()
    .typeError("Please enter a valid number")
    .required("Please enter your years of experience")
    .min(0, "Experience cannot be negative")
    .max(100, "Please enter a realistic experience (max 100 years)"),
  bio: Yup.string().required("Please enter bio").trim(),
  reason: Yup.string().required("Please enter reason").trim(),
  tagline: Yup.string().required("Please enter a tagline").trim(),
  profile_image: Yup.mixed()
    .required("Please upload your profile photo")
    .test("fileType", "Only JPG, JPEG, PNG files are allowed", (value) => {
      if (!value) return false;
      return ["image/jpg", "image/jpeg", "image/png"].includes(value.type);
    })
    .test("fileSize", "File size must be less than 2MB", (value) => {
      if (!value) return false;
      return value.size <= 2 * 1024 * 1024;
    }),
  cv: Yup.array()
    .required("Please upload at least one file")
    .min(1, "Please upload at least one file")
    .test("fileType", "Only PDF or Image files are allowed", (files) => {
      if (!files || files.length === 0) return false;

      const allowed = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
      ];

      return files.every((file) => allowed.includes(file.type));
    })
    .test("fileSize", "Each file must be less than 2 MB", (files) => {
      if (!files || files.length === 0) return false;

      return files.every((file) => file.size <= 2 * 1024 * 1024);
    }),

  certificate: Yup.array()
    .required("Please upload at least one file")
    .min(1, "Please upload at least one file")
    .test("fileType", "Only PDF or Image files are allowed", (files) => {
      if (!files || files.length === 0) return false;

      const allowed = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
      ];

      return files.every((file) => allowed.includes(file.type));
    })
    .test("fileSize", "Each file must be less than 2 MB", (files) => {
      if (!files || files.length === 0) return false;

      return files.every((file) => file.size <= 2 * 1024 * 1024);
    }),
});

export const updateProfileSchema = Yup.object().shape({
  email: emailValidation,
  full_name: fullNameValidation,
  phone_number: PhoneValidation,
  bio: Yup.string().required("Please enter bio").trim(),
  reason: Yup.string().required("Please enter reason").trim(),
  tagline: Yup.string().required("Please enter a tagline").trim(),
  year_of_experience: Yup.number()
    .typeError("Please enter a valid number")
    .required("Please enter your years of experience")
    .min(0, "Experience cannot be negative")
    .max(100, "Please enter a realistic experience (max 100 years)"),

  gender: Yup.string().nullable().trim(),

  dob: Yup.string().nullable().trim(),

  language: Yup.string().nullable().trim(),

  address_1: Yup.string().nullable().trim(),

  address_2: Yup.string().nullable().trim(),

  city: Yup.string().nullable().trim(),

  country: Yup.string().nullable().trim(),

  state: Yup.string().nullable().trim(),

  zip_code: Yup.string().nullable().trim(),
});

export const registerFindAJobSchema = Yup.object().shape({
  first_name: firstNameValidation,
  last_name: lastNameValidation,
  email: emailValidation,
  password: passwordValidation,
  confirm_password: Yup.string()
    .required("Please enter confirm password")
    .oneOf([Yup.ref("password"), null], "Your password must match"),
});

export const createOfferSchema = Yup.object().shape({
  offer_title: Yup.string().required("Please enter offer title").trim(),
  purpose: Yup.string().required("Please enter purpose").trim(),
  offer_type: Yup.string().required("Please select offer type").trim(),
  offer_format: Yup.string().required("Please select offer format").trim(),
  duration: Yup.string().required("Please select duration").trim(),
  price: Yup.number()
    .typeError("Price must be a number")
    .required("Please enter price")
    .positive("Price must be greater than zero")
    .min(1, "Price must be at least 1")
    .integer("Price must be a valid number"),
  additional_notes: Yup.string().required("Please select duration").trim(),
});

export const profileStep1Schema = Yup.object().shape({
  talent_role: Yup.string().required("Please enter role").trim(),
  bio: Yup.string().required("Please enter bio").trim(),
  location: Yup.string().required("Please enter location").trim(),
});

export const profileStep2Schema = Yup.object().shape({
  skills: Yup.array()
    .of(Yup.string().trim().min(1, "Cannot be empty"))
    .min(1, "Please add at least one skill")
    .required("Please add at least one skill"),
  language: Yup.string().required("Please enter language").trim(),
  certificates: Yup.array()
    .test("fileType", "Only PDF or Image files are allowed", (files) => {
      if (!files || files.length === 0) return true;

      const allowed = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
      ];

      return files.every((file) => allowed.includes(file.type));
    })
    .test("fileSize", "Each file must be less than 2 MB", (files) => {
      if (!files || files.length === 0) return true;

      return files.every((file) => file.size <= 2 * 1024 * 1024);
    }),
});

export const profileStep3Schema = Yup.object().shape({
  cv: Yup.array()
    .min(1, "Please upload at least one CV")
    .test(
      "fileType",
      "Only PDF or image files are allowed",
      (files) => {
        if (!files || !files.length) return true;
        return files.every((file) => {
          if (file instanceof File) {
            return (
              file.type.includes("pdf") ||
              file.type.includes("image")
            );
          }
          if (typeof file === "string") {
            return (
              file.toLowerCase().endsWith(".pdf") ||
              file.toLowerCase().match(/\.(jpg|jpeg|png|webp)$/)
            );
          }
          return false;
        });
      }
    ),
  projects: Yup.array()
    .required("Please upload at least 3 project files (ZIP)")
    .min(3, "Please upload at least 3 project files (ZIP)")
    .test(
      "fileType",
      "Only ZIP files are allowed",
      (files) => {
        if (!Array.isArray(files) || files.length === 0) return false;
        return files.every((file) => {
          if (file instanceof File) {
            return (
              file.type === "application/zip" ||
              file.name?.toLowerCase().endsWith(".zip")
            );
          }
          if (typeof file === "string") {
            return file.toLowerCase().endsWith(".zip");
          }
          return false;
        });
      }
    )
    .test(
      "fileSize",
      "Each file must be less than 20 MB",
      (files) => {
        if (!Array.isArray(files) || files.length === 0) return false;
        return files.every((file) => {
          if (file instanceof File) {
            return file.size <= 20 * 1024 * 1024;
          }
          if (typeof file === "string") {
            return true;
          }
          return false;
        });
      }
    ),
  website_or_portfolio: Yup.string()
    .required("Please enter Portfolio or Website")
    .trim(),
});

export const profileStep4Schema = Yup.object().shape({
  job_type: Yup.string().required("Please select a job type"),
  search_status: Yup.string().required("Please select job status"),
  salary_preference: Yup.string().required("Salary preference is required"),
});

export const createContentSchema = Yup.object().shape({
  title: Yup.string().required("Please enter title").trim(),
  content_type: Yup.string().required("Please select content type"),
  visibility: Yup.string().required("Please select visibility"),
  status: Yup.string().required("Please select status"),
  short_description: Yup.string().required("Please enter short description"),

  files: Yup.array()
    .required("Please upload at least one file")
    .min(1, "Please upload at least one file")

    .test("fileType", "Only PDF or Image files are allowed", (files) => {
      if (!files || files.length === 0) return true; // let required/min handle it

      const allowed = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
      ];

      return files.every((file) => {
        if (!file) return false;

        // 1) Existing backend value as string (URL) → always ok
        if (typeof file === "string") return true;

        // 2) New upload (File object)
        if (file instanceof File) {
          return allowed.includes(file.type);
        }

        // 3) Existing backend object: { content_file_id, file: "url", ... }
        if (typeof file === "object" && (file.file || file.url)) {
          return true; // trust backend, already uploaded
        }

        return false;
      });
    })
    .test("fileSize", "Each file must be less than 2 MB", (files) => {
      if (!files || files.length === 0) return true;

      return files.every((file) => {
        if (!file) return false;

        // 1) Existing string URL → skip size check
        if (typeof file === "string") return true;

        // 2) New upload (File object) → check size
        if (file instanceof File) {
          return file.size <= 2 * 1024 * 1024;
        }

        // 3) Existing backend object (no size property) → skip size check
        if (typeof file === "object" && (file.file || file.url)) {
          return true;
        }

        return true;
      });
    }),
  image: Yup.array()
    .required("Please upload image")
    .min(1, "Please upload at least one image")
    .test("fileType", "Only PDF or Image files are allowed", (files) => {
      if (!files || files.length === 0) return true; // let required/min handle it
      const allowed = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
      ];
      return files.every((file) => {
        if (!file) return false;

        // 1) Existing backend value as string (URL) → always ok
        if (typeof file === "string") return true;

        // 2) New upload (File object)
        if (file instanceof File) {
          return allowed.includes(file.type);
        }

        // 3) Existing backend object: { content_file_id, file: "url", ... }
        if (typeof file === "object" && (file.file || file.url)) {
          return true; // trust backend, already uploaded
        }

        return false;
      });
    })
    .test("fileSize", "Each file must be less than 2 MB", (files) => {
      if (!files || files.length === 0) return true;

      return files.every((file) => {
        if (!file) return false;

        // 1) Existing string URL → skip size check
        if (typeof file === "string") return true;

        // 2) New upload (File object) → check size
        if (file instanceof File) {
          return file.size <= 2 * 1024 * 1024;
        }

        // 3) Existing backend object (no size property) → skip size check
        if (typeof file === "object" && (file.file || file.url)) {
          return true;
        }

        return true;
      });
    }),
});
export const updateFreelancerProfileSchema = Yup.object().shape({
  email: emailValidation,
  first_name: firstNameValidation,
  last_name: lastNameValidation,
  talent_role: Yup.string().required("Please enter role").trim(),
  location: Yup.string().required("Please enter location").trim(),
  bio: Yup.string().required("Please enter bio").trim(),
  skills: Yup.array()
    .of(Yup.string().trim().min(1, "Cannot be empty"))
    .min(1, "Please add at least one skill")
    .required("Please add at least one skill"),
  // language: Yup.string().required("Please enter language").trim(),

  website_or_portfolio: Yup.string()
    .required("Please enter Portfolio or Website")
    .trim(),
  dob: Yup.string().nullable().trim(),
  gender: Yup.string().nullable().trim(),
  phone_number: Yup.string().required("Please enter phone number"),
  city: Yup.string().nullable().trim(),
  country: Yup.string().nullable().trim(),
  state: Yup.string().nullable().trim(),
  address_1: Yup.string().nullable().trim(),
  address_2: Yup.string().nullable().trim(),
  zip_code: Yup.string().nullable().trim(),
  job_type: Yup.string().required("Please select a job type"),
  search_status: Yup.string().required("Please select job status"),
  salary_preference: Yup.string().required("Salary preference is required"),
});

export const hireTalentCompanyDetailSchema = Yup.object().shape({
  number_of_employees: Yup.string()
    .matches(/^[1-9][0-9]*$/, "Enter a valid number greater than 0")
    .required("Number of employees is required"),
  company_name: Yup.string().required("Please enter company name").trim(),
  company_bio: Yup.string().required("Please enter company about").trim(),
  company_website: Yup.string()
    .matches(
      /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/.*)?$/,
      "Enter a valid website URL"
    )
    .required("Please enter company website"),
});

export const reserveYourStopSchema = Yup.object().shape({
  email: emailValidation,
  name: Yup.string().trim().required("Please enter name"),
  notes: Yup.string().trim().optional(),
});

export const jobPostSchema = Yup.object().shape({
  job_title: Yup.string().trim().required("Please enter job title"),
  location: Yup.string().trim().required("Please enter location"),
  industry: Yup.string().trim().required("Please enter industry"),
  category: Yup.string().trim().required("Please enter category"),
  candidate_location: Yup.string().trim().required("Please select candidate location"),
  candidate_availability: Yup.string().trim().required("Please select candidate availability"),
  license: Yup.string().trim().required("Please select license"),
  job_description: Yup.string().trim().required("Please enter job description"),
  about_role: Yup.string().trim().required("Please enter about role"),
  responsibility: Yup.string().trim().required("Please enter responsibility"),
  education: Yup.string().trim().required("Please enter education"),
  years_of_experience: Yup.number()
    .typeError("Years of experience must be a number")
    .required("Please enter years of experience")
    .moreThan(0, "Years of experience must be greater than 0"),
  minimum_education: Yup.string().trim().required("Please select minimum education"),
  salary_currency: Yup.string().trim().required("Please select salary currency"),
  min_salary: Yup.number()
    .typeError("Min salary must be a number")
    .required("Please enter min salary")
    .moreThan(0, "Min salary must be greater than 0"),
  max_salary: Yup.number()
    .typeError("Max salary must be a number")
    .required("Please enter max salary")
    .moreThan(
      Yup.ref("min_salary"),
      "Max salary must be greater than min salary"
    ),
  start_date: Yup.date()
    .typeError("Please enter valid start date")
    .required("Please enter start date"),
  end_date: Yup.date()
    .typeError("Please enter valid end date")
    .nullable()
    .min(
      Yup.ref("start_date"),
      "End date must be after start date"
    ),
  skills: Yup.array()
    .of(Yup.string().trim().min(1, "Cannot be empty"))
    .min(1, "Please add at least one skill")
    .required("Please add at least one skill"),
  qualification: Yup.array()
    .of(Yup.string().trim().min(1, "Cannot be empty"))
    .min(1, "Please add at least one qualification")
    .required("Please add at least one qualification"),
  requirement: Yup.array()
    .of(Yup.string().trim().min(1, "Cannot be empty"))
    .min(1, "Please add at least one requirement")
    .required("Please add at least one requirement"),
});

export const addEditProjectSchema = Yup.object().shape({
  project_title: Yup.string().trim().required("Please enter project title"),
  project_description: Yup.string().trim().required("Please enter project description"),
  // project_categories: Yup.string().required("Please enter project categories"),
  role_in_project: Yup.string().trim().required("Please enter project role"),
  project_duration: Yup.string()
    .trim()
    .required("Please enter project duration")
    .matches(/^[0-9]+$/, "Only numbers are allowed")
    .test(
      "not-zero",
      "Project duration must be greater than 0",
      (value) => Number(value) > 0
    ),
  budget: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value
    )
    .typeError("Budget must be a number")
    .required("Please enter budget")
    .moreThan(0, "Budget must be greater than 0"),
  start_date: Yup.date()
    .typeError("Please select valid start date")
    .required("Please select start date"),
  project_categories: Yup.array()
    .of(Yup.string().trim().min(1, "Cannot be empty"))
    .min(1, "Please add at least one skill")
    .required("Please add at least one skill"),
  team_member_required: Yup.string()
    .trim()
    .required("Please enter team member")
    .matches(/^[0-9]+$/, "Only numbers are allowed")
    .test(
      "not-zero",
      "Team member must be greater than 0",
      (value) => Number(value) > 0
    ),
});

export const uploadDeliveablesSchema = Yup.object().shape({
  files: Yup.array()
    .required("Please upload at least 1 project files (ZIP)")
    .min(1, "Please upload at least 1 project files (ZIP)")
    .test("fileType", "Only ZIP files are allowed", (files) => {
      if (!files || files.length === 0) return false;
      return files.every(
        (file) =>
          file.type === "application/zip" ||
          file.name.toLowerCase().endsWith(".zip")
      );
    }),
  // .test("fileSize", "Each file must be less than 20 MB", (files) => {
  //   if (!files || files.length === 0) return false;
  //   return files.every((file) => file.size <= 20 * 1024 * 1024);
  // }),
  link: Yup.string()
    .nullable()
    .notRequired()
    .test(
      "is-valid-url",
      "Please enter a valid link",
      (value) => {
        if (!value) return true;
        return /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/.test(value);
      }
    ),
  comment: Yup.string()
    .nullable()
    .notRequired(),
});

export const sendMessageSchema = Yup.object().shape({
  text: Yup.string()
    .required("Please enter a message")
    .trim(),
});
